// PersonaFormModal.jsx
//
// Create or edit a behavioral persona for a specific model. Behavioral
// personas have a detection_rule (JSON) that captures the pattern -
// e.g. {"type":"pays_on_dow","dow":5} for "Pays on Friday", or
// {"type":"pays_near_day_of_month","day":1,"window":3} for "Pays
// near the 1st".
//
// The detection_rule field is a JSON textarea with validation. Common
// presets are offered as quick-fill buttons above the textarea to
// reduce typing for the common cases.

import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, Typography,
} from "@mui/material";

import {
  createBehavioralPersona, updateBehavioralPersona,
} from "../../../services/forecast/forecastservices";

const BLUE = "#378ADD";

const PRESETS = [
  {
    label: "Pays on Friday",
    rule: { type: "pays_on_dow", dow: 5 },
  },
  {
    label: "Pays near the 1st",
    rule: { type: "pays_near_day_of_month", day: 1, window: 3 },
  },
  {
    label: "Pays near the 15th",
    rule: { type: "pays_near_day_of_month", day: 15, window: 3 },
  },
  {
    label: "Same-day payer",
    rule: { type: "same_day_payment" },
  },
  {
    label: "Pays near holiday",
    rule: { type: "pays_near_holiday", window: 5 },
  },
];


export default function PersonaFormModal({ open, mode, modelId, initialData, onClose, onSaved }) {
  const isEdit = mode === "edit";

  const [name,         setName]         = useState("");
  const [description,  setDescription]  = useState("");
  const [ruleJson,     setRuleJson]     = useState("{}");
  const [displayOrder, setDisplayOrder] = useState(100);
  const [isActive,     setIsActive]     = useState(true);

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setSaving(false);

    if (isEdit && initialData) {
      setName(initialData.name || "");
      setDescription(initialData.description || "");
      setDisplayOrder(initialData.display_order ?? 100);
      setIsActive(initialData.is_active !== false);
      try {
        const cfg = typeof initialData.detection_rule === "string"
          ? JSON.parse(initialData.detection_rule)
          : (initialData.detection_rule || {});
        setRuleJson(JSON.stringify(cfg, null, 2));
      } catch {
        setRuleJson(initialData.detection_rule || "{}");
      }
    } else {
      setName("");
      setDescription("");
      setDisplayOrder(100);
      setIsActive(true);
      setRuleJson(JSON.stringify({ type: "pays_on_dow", dow: 5 }, null, 2));
    }
  }, [open, isEdit, initialData]);

  // ── Validate JSON ────────────────────────────────────────────
  let parsedRule = null;
  let ruleError  = "";
  try {
    parsedRule = JSON.parse(ruleJson);
    if (typeof parsedRule !== "object" || parsedRule === null) {
      ruleError = "Detection rule must be a JSON object";
      parsedRule = null;
    }
  } catch {
    ruleError = "Detection rule must be valid JSON";
  }

  const applyPreset = (preset) => {
    setRuleJson(JSON.stringify(preset.rule, null, 2));
    if (!isEdit && !name) setName(preset.label);
  };

  const handleSave = async () => {
    if (!name.trim())                                  { setError("Name is required"); return; }
    if (ruleError)                                     { setError(ruleError); return; }
    if (!isEdit && !modelId)                           { setError("Missing modelId"); return; }

    setSaving(true); setError("");
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        detection_rule: parsedRule,
        display_order: Number(displayOrder),
        is_active: isActive,
      };

      if (isEdit) {
        await updateBehavioralPersona(initialData.id, payload);
      } else {
        await createBehavioralPersona(modelId, payload);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 500 }}>
        {isEdit ? "Edit Behavioral Persona" : "New Behavioral Persona"}
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.6fr", gap: 2, mb: 2 }}>
          <TextField
            label="Name" required size="small" fullWidth autoFocus={!isEdit}
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={saving}
          />
          <TextField
            label="Order" type="number" size="small" fullWidth
            value={displayOrder}
            onChange={e => setDisplayOrder(Number(e.target.value))}
            disabled={saving}
          />
        </Box>

        <TextField
          label="Description" size="small" fullWidth multiline rows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
          disabled={saving}
          sx={{ mb: 2 }}
        />

        {/* Quick presets */}
        <Typography sx={{
          fontSize: 11, color: "text.secondary", mb: 1,
          textTransform: "uppercase", letterSpacing: "0.05em",
        }}>
          Quick presets
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
          {PRESETS.map(preset => (
            <Box
              key={preset.label}
              onClick={() => applyPreset(preset)}
              sx={{
                px: "10px", py: "3px", fontSize: 11, borderRadius: "20px",
                cursor: "pointer", userSelect: "none",
                border: "0.5px solid", borderColor: "divider",
                color: BLUE,
                transition: "all .15s",
                "&:hover": { borderColor: BLUE, background: `${BLUE}10` },
              }}
            >
              {preset.label}
            </Box>
          ))}
        </Box>

        <TextField
          label="Detection rule (JSON)" size="small" fullWidth
          multiline minRows={5} maxRows={12}
          value={ruleJson}
          onChange={e => setRuleJson(e.target.value)}
          disabled={saving}
          inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
          error={!!ruleError}
          helperText={ruleError || "JSON object describing the behavior pattern. Picking a preset above fills this in."}
        />

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !!ruleError}
          variant="contained"
          sx={{
            textTransform: "none", borderRadius: "20px",
            background: BLUE, "&:hover": { background: "#2A6FB0" },
          }}
        >
          {saving ? "Saving…" : (isEdit ? "Save changes" : "Create persona")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}